import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { BdPageable, BdPageableHelper, Paginator } from '@bindoc-projects/platform-lib/data';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BdPermissionsUsersAdminStore } from '../store/users/users-admin.store';
import { BdGraphApiUser } from './user.model';
import { UserCreationBody } from '@bindoc-projects/util-domain-lib';
import { BdCompany, GroupMemberType } from '@bindoc-projects/resource-domain-lib';
import { ApiEndpointKey } from '@bindoc-projects/app-config-domain-lib';
import {
  BdOwnerChainStarted,
  BdOwnerDeleteOutcome,
  BdOwnerDeletionCheck,
  bdOwnerDeletionCheckOf,
  bdOwnerDeleteRejection,
  bdOwnerDeleteStarted,
} from '../deletion/owner-deletion.model';

@Injectable({
  providedIn: 'root',
})
export class BdPermissionsUsersAdminService {
  private _httpClient = inject(HttpClient);
  private _environmentService = inject(BdAppEnvironmentService);

  private _store: BdPermissionsUsersAdminStore;

  constructor() {
    this._store = new BdPermissionsUsersAdminStore(
      this._environmentService.load(ApiEndpointKey.BD_PERMISSION_RESOURCE_URL),
    );
  }

  public create(user: UserCreationBody): Observable<BdGraphApiUser> {
    const url = this._store.users();

    return this._httpClient.post<BdGraphApiUser>(url, user);
  }

  public search(page?: Paginator): Observable<BdPageable<BdGraphApiUser>> {
    const url = this._store.usersAll();
    const queryParams = page?.appendTo(new HttpParams()) ?? new HttpParams();

    return this._httpClient
      .get<BdPageable<BdGraphApiUser>>(url, { params: queryParams })
      .pipe(
        map((response) =>
          BdPageableHelper.responseToPage(response, (item) => item),
        ),
      );
  }

  public getById(id: string): Observable<BdGraphApiUser | null> {
    const url = this._store.userById(id);
    return this._httpClient.get<BdGraphApiUser>(url);
  }

  /**
   * Updates a user's name via `PUT /users/{userId}`.
   *
   * Sends **only** the name fields. `isAccountEnabled` is deliberately omitted:
   * verified against the API, an absent field leaves the lock state untouched,
   * while a present one is applied — so echoing a value back would let a stale
   * read silently lock or unlock the account. Omitting it makes that impossible
   * rather than merely unlikely. `UpdateUserRequest` marks no field required.
   *
   * Use {@link enable} / {@link disable} to change the lock state deliberately.
   */
  public update(user: UserProfileUpdate): Observable<BdGraphApiUser> {
    const url = this._store.userById(user.id);

    return this._httpClient.put<BdGraphApiUser>(url, {
      givenName: user.givenName,
      surname: user.surname,
    });
  }

  /**
   * Locks the account via the dedicated endpoint, which takes no request body.
   *
   * This deliberately does NOT use `PUT /users/{userId}`: that endpoint fully
   * replaces `givenName`/`surname`/`isAccountEnabled`, so locking through it
   * required callers to supply names they usually do not have — and passing the
   * wrong ones silently overwrote the user's real name.
   */
  public disable(userId: string): Observable<BdGraphApiUser> {
    const url = this._store.userDisable(userId);

    return this._httpClient.post<BdGraphApiUser>(url, null);
  }

  /**
   * Unlocks the account. See {@link disable} for why this is not a `PUT`.
   */
  public enable(userId: string): Observable<BdGraphApiUser> {
    const url = this._store.userEnable(userId);

    return this._httpClient.post<BdGraphApiUser>(url, null);
  }

  /**
   * Changes the address the user signs in with, via the dedicated
   * `PUT /users/{userId}/login-email`.
   *
   * Deliberately **not** part of {@link update}: this is a credential change and
   * must never happen as a side effect of saving a profile. Per ADR-0019 it also
   * becomes the address the platform delivers to, while the B2C contact `mail`
   * is left untouched.
   *
   * Documented failures — the caller has to map these to copy, since the API
   * returns no client-safe message: **409** the address is already taken,
   * **422** the account is not a B2C local account (it has no `emailAddress`
   * identity), **400** the address is malformed.
   */
  public changeLoginEmail(
    userId: string,
    loginEmail: string,
  ): Observable<BdGraphApiUser> {
    const url = this._store.userLoginEmail(userId);

    return this._httpClient.put<BdGraphApiUser>(url, { loginEmail });
  }

  /** Which content still blocks deleting the user (`GET /users/{id}/deletion-check`). */
  public deletionCheck(userId: string): Observable<BdOwnerDeletionCheck> {
    return this._httpClient.get<BdOwnerDeletionCheck>(
      this._store.userDeletionCheck(userId),
    );
  }

  /**
   * Starts the irreversible purge of all content the user owns. Virtual
   * clinics move to the group "Benchmark-Kliniken" instead. Follow the
   * returned chain with `BdJobChainTracker.trackChain`.
   */
  public startContentPurge(userId: string): Observable<BdOwnerChainStarted> {
    return this._httpClient.post<BdOwnerChainStarted>(
      this._store.userContentPurge(userId),
      null,
    );
  }

  /**
   * `DELETE /users/{id}`: starts the delete chain (DB step, reference scrub,
   * B2C). The user stays as a tombstone. 404 and 409 answers come back as
   * outcomes (a 409 not-empty with its deletion check); every other error
   * stays an error.
   */
  public delete(
    userId: string,
  ): Observable<BdOwnerDeleteOutcome<BdOwnerDeletionCheck>> {
    return this._httpClient
      .delete<BdOwnerChainStarted | null>(this._store.userById(userId), {
        observe: 'response',
      })
      .pipe(
        map((response) => bdOwnerDeleteStarted(response)),
        catchError((error: unknown) => {
          const rejection = bdOwnerDeleteRejection(
            error,
            bdOwnerDeletionCheckOf,
          );
          return rejection ? of(rejection) : throwError(() => error);
        }),
      );
  }

  public getCompanyMemberships(userId: string): Observable<BdCompany[]> {
    const url = this._store.userMembershipCompanies(userId);

    return this._httpClient.get<BdCompany[]>(url);
  }

  public getGroupMemberships(userId: string): Observable<
    {
      groupId: string;
      type: GroupMemberType;
      userId: string;
    }[]
  > {
    const url = this._store.userMembershipGroups(userId);

    return this._httpClient.get<
      {
        groupId: string;
        type: GroupMemberType;
        userId: string;
      }[]
    >(url);
  }
}

/**
 * A user's editable name, for {@link BdPermissionsUsersAdminService.update}.
 *
 * Carries no `isAccountEnabled`: the lock state is changed only through the
 * dedicated enable/disable endpoints, so a name edit cannot reach it.
 */
export interface UserProfileUpdate {
  id: string;
  givenName: string;
  surname: string;
}
